/**
 * PACKAGES (RFC 0280 S3, renamed in RFC 0284 S6j) — one place that answers what
 * is installed. Discover opens as a pane (RFC 0279 S4).
 *
 * IT WAS CALLED APPS, and that stopped being true once chrome arrived here. An
 * app is now ONE KIND of package beside a sidebar, a header, a thread renderer
 * and a layout, so naming the container after one of its kinds made the other
 * four look like exceptions. `AppSummary` keeps its name because the catalog
 * entry genuinely is an app record; the SURFACE is about packages.
 *
 * It is a MODAL, not a pane. A pane splits, sits beside a conversation and
 * survives a reload; browsing for a package needs none of that. You do it once,
 * alone, looking at the project you are installing into, which is the same
 * test that keeps settings and the sidebar out of the content tree (RFC 0266).
 *
 * The manager follows RFC 0279's design canvas: one scan of the installed
 * count and scope, then compact rows. Updates (S7) sit above them, and only
 * when the registry has a newer version: no inert tab, no fabricated count.
 *
 * NO WORKERS FILTER, for the same reason. `workerName` lives on `AppDoc` and
 * not on the thin catalog entry, so this surface cannot tell which packages
 * have one and a Workers chip would match nothing forever.
 */

import type { AppSummary, Published } from '@mod/core/model'
import {
  AppWindow,
  ArrowUpRight,
  MoreHorizontal,
  Search,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { AppIcon } from '../../services/apps/AppIcon'
import { appAsFile } from '../../shell/window/sidebarItems'
import type { Subject } from '../../shell/window/subject'
import { AppVersionsView, useAppVersionRow } from './AppVersions'
import type { PackageUpdate } from './packageUpdates'

/**
 * What KIND of package a row is.
 *
 * EVERY ROW GETS ONE, including 'App'. Leaving the ordinary case unlabelled
 * made a poll app the only row with no kind, which reads as "unknown" rather
 * than as "app" — and it made the four chrome kinds look like the special ones
 * when they are simply the other four.
 *
 * These are also the filter chips, so the list of kinds and the list of filters
 * cannot drift.
 */
export const PACKAGE_KINDS = [
  'App',
  'Sidebar',
  'Header',
  'Composer',
  'Thread',
  'Layout',
] as const

export type AppRole = (typeof PACKAGE_KINDS)[number]

export function PackagesModal({
  apps,
  roleOf,
  statusOf,
  onOpen,
  onRemove,
  onChangeIcon,
  onOpenSkill,
  onPublish,
  onCapturePane,
  panes = [],
  onDiscover,
  sourceOrigin,
  updates = [],
  onUpdate,
  projectId,
  onClose,
}: {
  /** The installed catalog, already projected by whoever owns the doc. */
  apps: AppSummary[]
  /** What each entry does, for the ones that are not ordinary apps (S6f). */
  roleOf?: (app: AppSummary) => AppRole
  /** Whether it is in use, and what using it means. Replaces the description
   *  for an entry whose description says nothing a person needs. */
  statusOf?: (app: AppSummary) => string | undefined
  /** Opening an app opens its VIEW, titled by the app name, in the canvas. */
  onOpen?: (file: Subject) => void
  /**
   * Uninstall, DECIDED BY THE SHELL rather than here (S6f).
   *
   * This used to call `unregisterApp` against the project for every row, which
   * silently did nothing for anything installed in the World: it edited a map
   * that never held the entry. Removing chrome also has to take it out of every
   * layout that names it first, which is knowledge a modal has no business
   * holding.
   */
  onRemove?: (app: AppSummary) => void
  /** Open Icons on this app (RFC 0332 3c). Absent while Icons is not
   *  installed, so the row offers nothing it cannot do. */
  onChangeIcon?: (app: AppSummary) => void
  /** Show what the agent is told about this app (RFC 0334 S4). Offered only
   *  on an app that has a skill document. */
  onOpenSkill?: (app: AppSummary) => void
  onPublish?: (
    app: AppSummary,
    page: {
      category: string
      about: string
      screenshots: string[]
      includeSource?: boolean
    },
  ) => Promise<Published>
  /** Where an app's source comes from (RFC 0279 3c): written in a chat,
   *  pushed from a folder, or none. Publish offers source only when there is
   *  some, and starts it checked for an app written in a chat. */
  sourceOrigin?: (app: AppSummary) => Promise<'conversation' | 'folder' | null>
  onCapturePane?: (paneId: string) => Promise<string>
  panes?: { id: string; title: string }[]
  onDiscover?: () => void
  /** Installed packages the registry has a newer version of (RFC 0279 S7). */
  updates?: PackageUpdate[]
  /** Take one. Nothing updates without this click. */
  onUpdate?: (update: PackageUpdate) => Promise<void>
  projectId?: string
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  const [versionsOf, setVersionsOf] = useState<AppSummary | null>(null)
  /** A KIND, or null for all of them. Null rather than an 'All' member, so the
   *  filter's absence is a state rather than a value every reader has to check
   *  for. */
  const [kind, setKind] = useState<AppRole | null>(null)
  const [publishingApp, setPublishingApp] = useState<AppSummary | null>(null)

  // Esc closes, because a modal that traps you is worse than no modal. The
  // shell's own Esc ladder never sees this: the listener is on the dialog and
  // stops there.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        if (publishingApp) setPublishingApp(null)
        else if (versionsOf) setVersionsOf(null)
        else onClose()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [onClose, publishingApp, versionsOf])

  const installed = useMemo(() => {
    const q = query.trim().toLowerCase()
    return apps.filter(
      (app) =>
        (!kind || roleOf?.(app) === kind) &&
        (!q ||
          `${app.name} ${app.description ?? ''} ${app.dataType ?? ''}`
            .toLowerCase()
            .includes(q)),
    )
  }, [apps, query, kind, roleOf])

  /**
   * The chips to offer: only kinds something here actually is.
   *
   * A chip that can never match is the same mistake as a disabled Publish
   * button. BOTH HALVES are counted, so filtering to Layout stays possible
   * while you are on Discover and have none installed.
   */
  const kinds = useMemo(() => {
    const present = new Set<AppRole>()
    for (const app of apps) {
      const role = roleOf?.(app)
      if (role) present.add(role)
    }
    return PACKAGE_KINDS.filter((k) => present.has(k))
  }, [apps, roleOf])

  /**
   * DROP A FILTER WHOSE CHIP HAS GONE.
   *
   * Filter to Sidebar, remove the last sidebar, and the chip disappears from
   * `kinds` while `kind` still holds it: both lists filter to nothing and there
   * is no chip left to click to clear it. An empty list with no visible way
   * back is a dead end, and the only escape was closing the modal.
   */
  useEffect(() => {
    if (kind && !kinds.includes(kind)) setKind(null)
  }, [kind, kinds])

  if (publishingApp && onPublish) {
    return (
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]">
        <button
          type="button"
          aria-label="Close packages"
          onClick={onClose}
          className="absolute inset-0 cursor-default bg-foreground/10"
        />
        <PublishSheet
          app={publishingApp}
          panes={panes}
          onCapturePane={onCapturePane}
          sourceOrigin={sourceOrigin}
          onPublish={(page) => onPublish(publishingApp, page)}
          onBack={() => setPublishingApp(null)}
        />
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]">
      <button
        type="button"
        aria-label="Close packages"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-foreground/10"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Packages"
        className="relative flex min-h-[460px] max-h-[75vh] w-full max-w-[760px] flex-col overflow-hidden rounded-[var(--radius-project)] border border-border bg-background shadow-lg"
      >
        <header className="flex flex-col border-border/60 border-b px-6 pt-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-semibold text-2xl text-foreground leading-tight tracking-tight">
                Your packages
              </h1>
              <p className="mt-1 max-w-[65ch] text-muted-foreground text-xs leading-normal">
                Apps and window pieces installed in this World.
              </p>
            </div>
            <label className="flex w-48 shrink-0 items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-3 py-2">
              <Search size={13} className="shrink-0 text-muted-foreground/60" />
              <input
                // biome-ignore lint/a11y/noAutofocus: the search is why it opened
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search installed"
                aria-label="Search packages"
                className="min-w-0 flex-1 bg-transparent text-foreground text-xs outline-none placeholder:text-muted-foreground/60"
              />
            </label>
          </div>

          <div className="mt-5 flex items-center gap-2">
            <div className="min-w-24 rounded-lg border border-border bg-muted/40 px-3 py-2">
              <strong className="block text-lg leading-none">
                {apps.length}
              </strong>
              <span className="mt-1 block text-[11px] text-muted-foreground">
                installed
              </span>
            </div>
            <div className="min-w-24 rounded-lg border border-border bg-muted/40 px-3 py-2">
              <strong className="block text-lg leading-none">
                {apps.filter((app) => app.scope === 'world').length}
              </strong>
              <span className="mt-1 block text-[11px] text-muted-foreground">
                in every project
              </span>
            </div>
            {onDiscover && (
              <button
                type="button"
                onClick={onDiscover}
                className="ml-auto text-xs hover:underline"
              >
                Discover ↗
              </button>
            )}
          </div>

          <div className="mt-5 flex items-center gap-3 border-border border-t py-3">
            <span className="font-medium text-xs">Installed</span>
            {kinds.length > 1 && (
              <>
                <span className="h-3.5 w-px bg-border" aria-hidden />
                {/* KINDS, only the ones something here actually is. A chip
                  that can never match reads as broken rather than as empty. */}
                <div className="flex flex-wrap gap-1">
                  {kinds.map((k) => (
                    <Chip
                      key={k}
                      label={k}
                      on={kind === k}
                      // CLICKING THE ACTIVE ONE CLEARS IT, which is the only
                      // way back with no All chip, and the thing a person
                      // tries first anyway.
                      onClick={() => setKind(kind === k ? null : k)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
          {versionsOf && projectId ? (
            <AppVersionsView
              projectId={projectId}
              appId={versionsOf.id}
              name={versionsOf.name}
              onBack={() => setVersionsOf(null)}
            />
          ) : (
            <div className="flex flex-col">
              {onUpdate && updates.length > 0 && !query.trim() && !kind && (
                <section aria-label="Updates" className="mb-3">
                  <h2 className="px-2 pt-3 pb-1 font-medium text-xs">
                    Updates
                  </h2>
                  {updates.map((update) => (
                    <UpdateRow
                      key={update.app.id}
                      update={update}
                      onUpdate={() => onUpdate(update)}
                    />
                  ))}
                </section>
              )}
              {installed.length === 0 ? (
                <Note>
                  {apps.length === 0
                    ? 'Nothing installed. Ask the agent to build something, or look in Discover.'
                    : 'Nothing matches that.'}
                </Note>
              ) : (
                installed.map((app) => (
                  <AppRow
                    key={app.id}
                    appId={app.id}
                    name={app.name}
                    description={app.description ?? app.dataType ?? ''}
                    // NOTHING TO OPEN for chrome. A sidebar has no view of its
                    // own and a layout has nothing to run, so offering to open
                    // one would be a promise the row cannot keep.
                    onOpen={
                      // NO CLASSIFIER means every row is an ordinary app,
                      // which is what a caller that does not classify is
                      // saying. Treating unclassified as unopenable would make
                      // the simplest usage the broken one.
                      onOpen && (roleOf?.(app) ?? 'App') === 'App'
                        ? () => {
                            onOpen(appAsFile(app))
                            onClose()
                          }
                        : undefined
                    }
                    role={roleOf?.(app)}
                    scope={app.scope}
                    status={statusOf?.(app)}
                    onRemove={onRemove ? () => onRemove(app) : undefined}
                    onChangeIcon={
                      onChangeIcon && (roleOf?.(app) ?? 'App') === 'App'
                        ? () => {
                            onChangeIcon(app)
                            onClose()
                          }
                        : undefined
                    }
                    onOpenSkill={
                      onOpenSkill && app.skillDocId
                        ? () => onOpenSkill(app)
                        : undefined
                    }
                    onPublish={
                      onPublish ? () => setPublishingApp(app) : undefined
                    }
                    {...(projectId && (roleOf?.(app) ?? 'App') === 'App'
                      ? {
                          versioned: { projectId, appId: app.id },
                          onVersions: () => setVersionsOf(app),
                        }
                      : {})}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * One package with a newer version in the registry.
 *
 * A version that reaches more says what BEFORE the click (RFC 0279 3f), so the
 * added reads and writes, and a move into the window's realm or onto live
 * documents, are on the row and the button names the grant.
 */
function UpdateRow({
  update,
  onUpdate,
}: {
  update: PackageUpdate
  onUpdate: () => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { reads, writes, realm, sync } = update.reaches
  const more = [
    ...(reads.length ? [`reads ${reads.join(', ')}`] : []),
    ...(writes.length ? [`writes ${writes.join(', ')}`] : []),
    // Plain words: these two are not capability names a person would know.
    ...(realm === 'host' ? ['runs in the window, outside its sandbox'] : []),
    ...(sync === 'documents' ? ['reads your documents live'] : []),
  ]
  return (
    <div className="flex items-center gap-2 border-border/60 border-b px-2 py-3 last:border-b-0">
      <AppIcon
        appId={update.app.id}
        size={32}
        fallback={
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-border bg-background">
            <AppWindow size={14} className="text-muted-foreground" />
          </span>
        }
      />
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-foreground text-xs leading-tight">
          {update.app.name}
          <span className="ml-2 font-normal text-muted-foreground">
            {update.from} → {update.to}
          </span>
        </span>
        <span className="mt-0.5 block text-muted-foreground text-xs leading-normal">
          {error ??
            (more.length
              ? `This version also ${more.join(' and ')}.`
              : 'Reaches nothing new.')}
        </span>
      </span>
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true)
          setError(null)
          try {
            await onUpdate()
          } catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause))
          } finally {
            setBusy(false)
          }
        }}
        className="shrink-0 rounded-full bg-foreground px-3 py-1 text-background text-xs disabled:opacity-50"
      >
        {busy ? 'Updating…' : more.length ? 'Allow and update' : 'Update'}
      </button>
    </div>
  )
}

/** One kind filter. A hairline pill, which is the only border §3c allows. */
function Chip({
  label,
  on,
  onClick,
}: {
  label: string
  on: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      // NAMED as the action, not as the word on it. The label collides with the
      // kind badge every matching row also carries, and "App" alone tells a
      // screen reader nothing about what pressing it does.
      aria-label={`Show only ${label}`}
      className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
        on
          ? 'border-foreground/40 bg-muted text-foreground'
          : 'border-border text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
    </button>
  )
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <p className="col-span-full text-muted-foreground text-xs leading-normal">
      {children}
    </p>
  )
}

/**
 * One row: a 32px icon tile, a name, and a sentence.
 *
 * The negative margin under the padding is what lets the hover tint bleed past
 * the text column without moving anything, which is the trick that makes a
 * borderless list feel like rows rather than paragraphs.
 */
function AppRow(
  props: Parameters<typeof AppRowView>[0] & {
    versioned?: { projectId: string; appId: string }
  },
) {
  const { versioned, ...rest } = props
  return versioned ? (
    <VersionedAppRow {...rest} versioned={versioned} />
  ) : (
    <AppRowView {...rest} />
  )
}

function VersionedAppRow({
  versioned,
  ...rest
}: Parameters<typeof AppRowView>[0] & {
  versioned: { projectId: string; appId: string }
}) {
  const { line, onInstall } = useAppVersionRow(
    versioned.projectId,
    versioned.appId,
  )
  return (
    <AppRowView
      {...rest}
      {...(line ? { line } : {})}
      {...(onInstall ? { onInstall } : {})}
    />
  )
}

function AppRowView({
  appId,
  name,
  description,
  role,
  scope,
  status,
  onOpen,
  onRemove,
  onChangeIcon,
  onOpenSkill,
  onPublish,
  onVersions,
  onInstall,
  line,
}: {
  /** The installed app this row is, so it draws that app's tile. */
  appId?: string
  name: string
  description: string
  role?: AppRole
  scope?: 'world' | 'project'
  status?: string
  onOpen?: () => void
  onRemove?: () => void
  onChangeIcon?: () => void
  onOpenSkill?: () => void
  onPublish?: () => void
  onVersions?: () => void
  onInstall?: () => void
  line?: string
}) {
  return (
    <div className="group/app flex items-center gap-2 border-border/60 border-b px-2 py-3 transition-colors last:border-b-0 hover:bg-muted/40">
      <button
        type="button"
        onClick={onOpen}
        disabled={!onOpen}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        {/* The app's own tile (RFC 0332), no bordered box around it. */}
        <AppIcon
          appId={appId}
          size={32}
          fallback={
            <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-background">
              <AppWindow size={14} className="text-muted-foreground" />
            </span>
          }
        />
        <span className="min-w-0">
          <span className="flex items-center gap-1 font-semibold text-foreground text-xs leading-tight">
            <span className="truncate">{name}</span>
            {/* ONLY WHEN THERE IS SOMEWHERE TO GO. A sidebar has no view to
              open and a layout has nothing to run, so the affordance would be
              a promise the row cannot keep. */}
            {onOpen && (
              <ArrowUpRight
                size={12}
                className="shrink-0 text-muted-foreground/60 opacity-0 transition-opacity group-hover/app:opacity-100"
              />
            )}
            {role && (
              <span className="ml-1 shrink-0 font-normal text-[10px] text-muted-foreground uppercase tracking-wide">
                {role}
              </span>
            )}
          </span>
          <span className="mt-0.5 block max-w-[65ch] truncate text-muted-foreground text-xs leading-normal">
            {line ?? status ?? description}
          </span>
        </span>
      </button>
      {scope && (
        <span className="shrink-0 text-[11px] text-muted-foreground">
          {scope === 'world' ? 'Every project' : 'This project'}
        </span>
      )}
      {onInstall && (
        <button
          type="button"
          onClick={onInstall}
          className="rounded-full bg-foreground px-3 py-1 text-background text-xs"
        >
          Install
        </button>
      )}
      {(onChangeIcon || onOpenSkill || onPublish || onVersions) && (
        <RowMenu
          name={name}
          onChangeIcon={onChangeIcon}
          onOpenSkill={onOpenSkill}
          onPublish={onPublish}
          onVersions={onVersions}
        />
      )}
      {onRemove && (
        <button
          type="button"
          title="Remove from this project"
          aria-label={`Remove ${name}`}
          onClick={onRemove}
          className="shrink-0 rounded p-1 text-muted-foreground/60 opacity-0 transition-opacity hover:bg-background hover:text-foreground group-hover/app:opacity-100"
        >
          <X size={12} />
        </button>
      )}
    </div>
  )
}

/**
 * A row's quiet actions behind ⋯, beside the ×. Change icon was a text button
 * that took a third of a two-column row for an action you use once; the menu
 * keeps the row to its name and says the rest on hover.
 */
function RowMenu({
  name,
  onChangeIcon,
  onOpenSkill,
  onPublish,
  onVersions,
}: {
  name: string
  onChangeIcon?: () => void
  onOpenSkill?: () => void
  onPublish?: () => void
  onVersions?: () => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        title="More"
        aria-label={`More for ${name}`}
        onClick={() => setOpen((o) => !o)}
        className={`rounded p-1 text-muted-foreground/60 transition-opacity hover:bg-background hover:text-foreground ${
          open ? '' : 'opacity-0 group-hover/app:opacity-100'
        }`}
      >
        <MoreHorizontal size={12} />
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 z-20 mt-1 w-32 overflow-hidden rounded-md border border-border bg-background py-1 shadow-lg">
            {onVersions && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  onVersions()
                }}
                className="block w-full px-3 py-1.5 text-left text-xs hover:bg-muted"
              >
                Versions
              </button>
            )}
            {onPublish && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  onPublish()
                }}
                className="block w-full px-3 py-1.5 text-left text-xs hover:bg-muted"
              >
                Publish…
              </button>
            )}
            {onOpenSkill && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  onOpenSkill()
                }}
                className="block w-full px-3 py-1.5 text-left text-xs hover:bg-muted"
              >
                Open skill
              </button>
            )}
            {onChangeIcon && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  onChangeIcon()
                }}
                className="block w-full px-3 py-1.5 text-left text-xs hover:bg-muted"
              >
                Change icon
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function PublishSheet({
  app,
  panes,
  sourceOrigin,
  onCapturePane,
  onPublish,
  onBack,
}: {
  app: AppSummary
  panes: { id: string; title: string }[]
  sourceOrigin?: (app: AppSummary) => Promise<'conversation' | 'folder' | null>
  onCapturePane?: (paneId: string) => Promise<string>
  onPublish: (page: {
    category: string
    about: string
    screenshots: string[]
    includeSource?: boolean
  }) => Promise<Published>
  onBack: () => void
}) {
  // RFC 0279 3c: CHECKED for an app an agent wrote in a chat, whose view and
  // skill are its source; UNCHECKED for a pushed folder, which is a person's
  // and may hold things they did not mean to share. No box at all with none.
  const [origin, setOrigin] = useState<'conversation' | 'folder' | null>(null)
  const [includeSource, setIncludeSource] = useState(false)
  // Keyed on the app alone: the shell passes a fresh function each render,
  // and re-running would reset a box the person already unticked.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    let live = true
    sourceOrigin?.(app)
      .then((found) => {
        if (!live) return
        setOrigin(found)
        setIncludeSource(found === 'conversation')
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [app.id])
  const [about, setAbout] = useState(app.description ?? '')
  const [category, setCategory] = useState('Work')
  const [paneId, setPaneId] = useState(panes[0]?.id ?? '')
  const [screenshots, setScreenshots] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [published, setPublished] = useState<Published | null>(null)

  const capture = async () => {
    if (!paneId || !onCapturePane || busy) return
    setBusy(true)
    setError(null)
    try {
      const url = await onCapturePane(paneId)
      setScreenshots((current) => [...current, url])
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const publish = async () => {
    if (busy || !category.trim()) return
    setBusy(true)
    setError(null)
    try {
      setPublished(
        await onPublish({
          category: category.trim(),
          about: about.trim(),
          screenshots,
          ...(origin ? { includeSource } : {}),
        }),
      )
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Publish ${app.name}`}
      className="relative w-full max-w-[600px] rounded-[var(--radius-project)] border border-border bg-background p-6 shadow-lg"
    >
      <button
        type="button"
        onClick={onBack}
        className="mb-5 text-muted-foreground text-xs hover:text-foreground"
      >
        ← Your packages
      </button>
      <h1 className="font-semibold text-2xl">Publish {app.name}</h1>
      {published ? (
        <div className="mt-5 space-y-3 text-sm">
          <p>
            {app.name} {published.version} is live in Discover.
          </p>
          <p className="text-muted-foreground text-xs">{published.lineageId}</p>
          <button
            type="button"
            onClick={onBack}
            className="rounded-lg bg-foreground px-4 py-2 text-background"
          >
            Done
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-5 text-sm">
          <p className="text-muted-foreground text-xs">
            Anyone can find and install this package. Your Mod account will be
            shown as its publisher.
          </p>
          <label className="block font-medium text-xs">
            About
            <textarea
              aria-label="About package"
              value={about}
              onChange={(e) => setAbout(e.target.value)}
              rows={3}
              className="mt-2 block w-full resize-y rounded-lg border border-border bg-background p-3 font-normal text-sm"
            />
          </label>
          <label className="block font-medium text-xs">
            Category
            <select
              aria-label="Package category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-2 block w-full rounded-lg border border-border bg-background p-2 font-normal text-sm"
            >
              {['Work', 'Developer', 'Creative', 'Personal', 'Other'].map(
                (value) => (
                  <option key={value}>{value}</option>
                ),
              )}
            </select>
          </label>
          {onCapturePane && panes.length > 0 && (
            <div>
              <span className="font-medium text-xs">Screenshots</span>
              <div className="mt-2 flex gap-2">
                <select
                  aria-label="Pane to capture"
                  value={paneId}
                  onChange={(e) => setPaneId(e.target.value)}
                  className="min-w-0 flex-1 rounded-lg border border-border bg-background p-2 text-xs"
                >
                  {panes.map((pane) => (
                    <option key={pane.id} value={pane.id}>
                      {pane.title}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void capture()}
                  className="rounded-lg border border-border px-3 text-xs disabled:opacity-50"
                >
                  Capture pane
                </button>
              </div>
              {screenshots.length > 0 && (
                <div className="mt-3 flex gap-2">
                  {screenshots.map((url) => (
                    <img
                      key={url}
                      src={url}
                      alt={`${app.name} screenshot`}
                      className="h-20 rounded-lg border border-border"
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          {origin && (
            <label className="flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                checked={includeSource}
                onChange={(e) => setIncludeSource(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium">Include source for forking</span>
                <span className="block text-muted-foreground">
                  {origin === 'conversation'
                    ? 'Anyone can download the view and skill your agent wrote, and change their own copy.'
                    : 'Anyone can download the files this app was built from.'}{' '}
                  Installing never needs them.
                </span>
              </span>
            </label>
          )}
          {error && (
            <p role="alert" className="text-red-500 text-xs">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 border-border border-t pt-4">
            <button
              type="button"
              onClick={onBack}
              className="rounded-lg px-4 py-2 text-xs"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={busy || !category.trim()}
              onClick={() => void publish()}
              className="rounded-lg bg-foreground px-4 py-2 text-background text-xs disabled:opacity-50"
            >
              {busy ? 'Working…' : 'Publish'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
